// The Projects page's tabs, region chips, year select and list/tiles toggle.
//
// Every row is server-rendered — 450 of them across three tabs — and filtering
// only toggles `hidden`. No fetch, no re-render: a chip click in the reference
// build is instant, and a round-trip would need a loading state it does not have.
//
// TILES CARRY NO .reveal CLASS. app.js initialises .reveal once at
// DOMContentLoaded, and under .awdd-anim a .reveal element is visibility:hidden
// in CSS until GSAP's autoAlpha tween un-hides it — so a .reveal added to an
// element created later never becomes visible. The reference re-runs useReveal
// on every state change, which also re-animates the whole grid on every chip
// click; not behaviour worth reproducing. The reference's per-tile
// transition-delay goes with it: it exists to stagger that reveal, and left in
// place it would only delay the hover transition.
//
// GOLD, NOT ACCENT, on the tile link. The reference's `accent` is gold and the
// tile sits on a fixed ink-deep gradient, so the hover is gold and unbranched.
//
// ROWS ARE NEVER REMOVED FROM THE DOM, only hidden — the tiles are built from
// their data-* attributes, so removing a row would destroy the data.
import { icon } from './icons.js';

// Panel slug -> the scopes that live inside it. Case Studies has two tables
// (Australian and export) that share one year value.
const PANEL_SCOPES = {
	'project-history': ['project-history'],
	'sintalock-history': ['sintalock-history'],
	'case-studies': ['case-studies-au', 'case-studies-export'],
};

const IMAGE_ICON = icon( 'image', 'h-[26px] w-[26px]' );

const TAB_ON_LIGHT = ['-mb-px', 'border-b-2', 'border-steel-500', 'text-ink'];
const TAB_OFF_LIGHT = ['text-mute2', 'hover:text-ink'];
const TAB_ON_DARK = ['-mb-px', 'border-b-2', 'border-steel-300', 'text-white'];
const TAB_OFF_DARK = ['text-mute2', 'hover:text-white'];

function escapeHtml(value) {
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

export default function initProjects() {
	const root = document.querySelector('[data-awdd-projects]');
	if (!root) return;

	const tabs = Array.from(root.querySelectorAll('[data-awdd-tab]'));
	const panels = Array.from(root.querySelectorAll('[data-awdd-panel]'));
	if (!tabs.length || !panels.length) return;

	// The tab buttons were rendered with whichever arm the block's tone chose,
	// so read the polarity off the markup rather than guessing it.
	const isDark = tabs.some((t) => t.classList.contains('border-steel-300') || t.classList.contains('hover:text-white'));
	const tabOn = isDark ? TAB_ON_DARK : TAB_ON_LIGHT;
	const tabOff = isDark ? TAB_OFF_DARK : TAB_OFF_LIGHT;

	const chips = Array.from(root.querySelectorAll('[data-awdd-chip]'));

	// Chip state classes are DERIVED, not hardcoded, so the block's light and
	// dark arms both work without this module knowing which one PHP rendered.
	//
	// They must be the state classes ONLY. An earlier version took every class
	// on an inactive chip except the active ones, which swept up the shared
	// base (rounded-full, border, px-3, py-1.5, font-sans, the text size and
	// transition-colors) into the "off" list -- so activating a chip removed
	// its padding and shape and left a 19px stub. The shared base is exactly
	// the intersection of an active and an inactive chip, so subtract that.
	const chipActive = chips.find((c) => c.getAttribute('aria-pressed') === 'true');
	const chipInactive = chips.find((c) => c.getAttribute('aria-pressed') !== 'true');
	const chipBase =
		chipActive && chipInactive
			? Array.from(chipActive.classList).filter((c) => chipInactive.classList.contains(c))
			: [];
	const chipOn = chipActive ? Array.from(chipActive.classList).filter((c) => !chipBase.includes(c)) : [];
	const chipOff = chipInactive ? Array.from(chipInactive.classList).filter((c) => !chipBase.includes(c)) : [];

	const state = { tab: tabs[0].getAttribute('data-awdd-tab'), region: 'all', year: 'all', view: 'list' };

	// ---- Deep links -------------------------------------------------------
	const params = new URLSearchParams(window.location.search);
	const wantedTab = params.get('tab');
	if (wantedTab && tabs.some((t) => t.getAttribute('data-awdd-tab') === wantedTab)) {
		state.tab = wantedTab;
	}
	const wantedRegion = params.get('state');
	if (wantedRegion && chips.some((c) => c.getAttribute('data-awdd-chip') === wantedRegion)) {
		state.region = wantedRegion;
	}

	function writeUrl() {
		const next = new URLSearchParams(window.location.search);
		next.set('tab', state.tab);
		if (state.region === 'all') {
			next.delete('state');
		} else {
			next.set('state', state.region);
		}
		window.history.replaceState(null, '', `${window.location.pathname}?${next}${window.location.hash}`);
	}

	// ---- Row matching -----------------------------------------------------
	function matches(row) {
		const region = state.region;
		if (region !== 'all') {
			// `export` is a bucket so it covers UAE, Singapore and Fiji at once.
			// Every other value — New Zealand included — matches by region.
			if (region === 'export') {
				if (row.getAttribute('data-bucket') !== 'export') return false;
			} else if (row.getAttribute('data-region') !== region) {
				return false;
			}
		}
		// data-years is the project's Year tags, space-separated: a "2016-17"
		// project matches both 2016 and 2017. data-year stays the label shown.
		if (state.year !== 'all' && !(row.getAttribute('data-years') || '').split(' ').includes(state.year)) return false;
		return true;
	}

	function tileFor(row) {
		const name = row.getAttribute('data-name') || '';
		const region = row.getAttribute('data-region') || '';
		const year = row.getAttribute('data-year') || '';
		const detail = row.getAttribute('data-detail') || '';
		const href = row.getAttribute('data-href') || '';
		const principal = row.getAttribute('data-principal') || '';
		const image = row.getAttribute('data-image') || '';

		const meta = [principal, region, year].filter(Boolean).map(escapeHtml).join(' • ');
		const title = href
			? `<a href="${escapeHtml(href)}"${href.startsWith('http') ? ' target="_blank" rel="noopener noreferrer"' : ''} class="mt-1 inline-flex font-display text-lg font-bold leading-tight tracking-tight underline decoration-white/30 underline-offset-2 transition-colors hover:decoration-gold hover:text-gold">${escapeHtml(name)}</a>`
			: `<h3 class="mt-1 font-display text-lg font-bold leading-tight tracking-tight">${escapeHtml(name)}</h3>`;

		// A photo becomes the tile's ground; without one the icon placeholder
		// stays, which is deliberate — 240 of the 334 projects have no photo of
		// their own, and a generic pipeline shot on a named project would
		// misstate a fact about that project.
		//
		// NO .reveal HERE. See the note at the top of this file: reveals are
		// initialised once at DOMContentLoaded, so .reveal on DOM built later is
		// hidden permanently.
		const ground = image
			? `<span class="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105" style="background-image:url('${escapeHtml(image)}')"></span>`
			: `<span class="absolute inset-0 flex items-center justify-center bg-fill text-mute2">${IMAGE_ICON}</span>`;

		return `
			<article class="group relative flex min-h-[220px] flex-col justify-end overflow-hidden rounded-xl p-4 text-white transition-all hover:shadow-card">
				${ground}
				<div class="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink-deep/95 via-ink-deep/45 to-ink-deep/10"></div>
				<div class="relative">
					<div class="font-sans text-[9px] uppercase tracking-wide text-white/70">${meta}</div>
					${title}
					<p class="mt-1 text-[12.5px] text-white/80">${escapeHtml(detail)}</p>
				</div>
			</article>`;
	}

	function renderScope(scope) {
		const list = root.querySelector(`[data-awdd-list="${scope}"]`);
		const tiles = root.querySelector(`[data-awdd-tiles="${scope}"]`);
		const empty = root.querySelector(`[data-awdd-empty="${scope}"]`);
		if (!list) return false;

		const rows = Array.from(list.querySelectorAll('[data-awdd-project-row]'));
		const visible = [];
		rows.forEach((row) => {
			const show = matches(row);
			row.hidden = !show;
			if (show) visible.push(row);
		});

		const isEmpty = visible.length === 0;
		if (empty) empty.hidden = !isEmpty;

		if (state.view === 'tiles') {
			list.hidden = true;
			if (tiles) {
				tiles.hidden = isEmpty;
				tiles.innerHTML = isEmpty ? '' : visible.map(tileFor).join('');
			}
		} else {
			list.hidden = isEmpty;
			if (tiles) {
				tiles.hidden = true;
				tiles.innerHTML = '';
			}
		}

		// Keep every toolbar for this scope in step with the state.
		root.querySelectorAll(`[data-awdd-scope="${scope}"]`).forEach((bar) => {
			const select = bar.querySelector('[data-awdd-year]');
			if (select && select.value !== state.year) select.value = state.year;
			bar.querySelectorAll('[data-awdd-view]').forEach((btn) => {
				const on = btn.getAttribute('data-awdd-view') === state.view;
				btn.setAttribute('aria-pressed', String(on));
				btn.classList.toggle('bg-steel-500', on);
				btn.classList.toggle('text-white', on);
				btn.classList.toggle('text-mute2', !on);
			});
		});

		return isEmpty;
	}

	function render() {
		tabs.forEach((tab) => {
			const on = tab.getAttribute('data-awdd-tab') === state.tab;
			tab.setAttribute('aria-selected', String(on));
			tab.classList.remove(...tabOn, ...tabOff);
			tab.classList.add(...(on ? tabOn : tabOff));
		});

		panels.forEach((panel) => {
			panel.hidden = panel.getAttribute('data-awdd-panel') !== state.tab;
		});

		chips.forEach((chip) => {
			const on = chip.getAttribute('data-awdd-chip') === state.region;
			chip.setAttribute('aria-pressed', String(on));
			chip.classList.remove(...chipOn, ...chipOff);
			chip.classList.add(...(on ? chipOn : chipOff));
		});

		const scopes = PANEL_SCOPES[state.tab] || [];
		const emptied = scopes.filter(renderScope);

		// Case Studies shares one chip row across two tables, so a region chip
		// routinely empties one of them (NSW has no export rows). Hide that
		// table's whole section, heading included, rather than leaving a heading
		// over "no projects match". Only when the OTHER table still has rows:
		// if the filters empty both, both sections stay so the empty-state
		// message is still there to explain the blank panel.
		const regionFiltered = state.region !== 'all';
		scopes.forEach((scope) => {
			const section = root.querySelector(`[data-awdd-section="${scope}"]`);
			if (!section) return;
			section.hidden = regionFiltered && emptied.includes(scope) && emptied.length < scopes.length;
		});

		writeUrl();
	}

	// ---- Bindings ---------------------------------------------------------
	tabs.forEach((tab) => {
		tab.addEventListener('click', () => {
			state.tab = tab.getAttribute('data-awdd-tab');
			// Reset both filters on a tab change, matching Projects.jsx:100-103 —
			// a year selected on one tab must not empty the next.
			state.region = 'all';
			state.year = 'all';
			state.view = 'list';
			root.querySelectorAll('[data-awdd-year]').forEach((s) => {
				s.value = 'all';
			});
			render();
		});
	});

	chips.forEach((chip) => {
		chip.addEventListener('click', () => {
			state.region = chip.getAttribute('data-awdd-chip');
			render();
		});
	});

	root.querySelectorAll('[data-awdd-year]').forEach((select) => {
		select.addEventListener('change', () => {
			state.year = select.value;
			render();
		});
	});

	root.querySelectorAll('[data-awdd-view]').forEach((btn) => {
		btn.addEventListener('click', () => {
			state.view = btn.getAttribute('data-awdd-view');
			render();
		});
	});

	// First run: this is where panels two and three are hidden for the first
	// time. Nothing is hidden server-side, so a dead bundle leaves them all
	// readable in document order.
	render();
}
